import React, { useEffect, useState } from "react";

import AddVessel from "./AddVessel";

import ApiClient from "../services/fuelandmoreapi";

import type {
  TCurrentVessel,
  TPort,
  TBunkerPrice,
  TWeather,
  TCrudePrice,
} from "@/types";

import PortPlanner from "./PortPlanner";

import BinIcon from "./BinIcon";

import VeselIcon from "./VesselIcon";

import AddFleet from "./AddFleet";

import { FeedbackText } from "./FeedbackText";

import RiskScoreDetailsModal from "./RiskScoreDetailsModal";

import { config } from "../config";

import "./ClientFleet.css";

interface Fleet {
  id: string;
  name: string;
  vessels: TCurrentVessel[];
}

interface BackendFleet {
  id: string;
  name: string;
  vessels: string[];
}

interface ClientFleetProps {
  onVesselClick: (lat: number, lng: number) => void;
  onShowAll: (vessels: TCurrentVessel[]) => void;
  onUpdateVessels: (vessels: TCurrentVessel[]) => void;
  setRouteCoordinates: React.Dispatch<
    React.SetStateAction<[number, number][][]>
  >;
  setRouteColors: React.Dispatch<React.SetStateAction<string[]>>;
  setBunkerMarkers: React.Dispatch<React.SetStateAction<TPort[][]>>;
  setPrices: React.Dispatch<React.SetStateAction<TBunkerPrice[]>>;
  setCrudePrice: React.Dispatch<React.SetStateAction<TCrudePrice | undefined>>;
  setWeather: React.Dispatch<React.SetStateAction<Map<string, TWeather[]>>>;
  distanceNm: number;
  onSelectPortCall: (
    vessel: TCurrentVessel,
    fromPort: string,
    destinationPort: string,
    eta: string,
  ) => void;
}

const ClientFleet: React.FC<ClientFleetProps> = ({
  onUpdateVessels,
  setRouteCoordinates,
  setRouteColors,
  setBunkerMarkers,
  setPrices,
  setCrudePrice,
  setWeather,
  distanceNm,
  onSelectPortCall,
}) => {
  const [fleets, setFleets] = useState<Fleet[]>([]);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [isAddVesselOpen, setIsAddVesselOpen] = useState(false);
  const [selectedFleet, setSelectedFleet] = useState<{
    fleetName: string;
    clientFleetId: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVesselForPlanning, setSelectedVesselForPlanning] =
    useState<TCurrentVessel | null>(null);
  // State kept minimal to avoid unused variable lint errors
  const [, setSelectedPortCallDetails] = useState<{
    destinationPort: string;
    eta: string;
  } | null>(null);
  const [, setHoveredVesselImo] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{
    fleetId: string;
    imo?: string;
  } | null>(null);
  // Add a separate state for fleet deletion confirmation
  const [confirmDeleteFleet, setConfirmDeleteFleet] = useState<{
    fleetId: string;
  } | null>(null);
  const [riskScoreDetailsModal, setRiskScoreDetailsModal] = useState<{
    open: boolean;
    details: any | null;
  }>({ open: false, details: null });

  const { apiBaseUrl } = config;
  const apiUrl = apiBaseUrl;

  const handleSelectPortCall = (
    fromPort: string,
    destinationPort: string,
    eta: string,
  ) => {
    setSelectedPortCallDetails({ destinationPort, eta });
    onSelectPortCall(
      selectedVesselForPlanning!,
      fromPort,
      destinationPort,
      eta,
    );
  };

  const formatDateToDDMM = (dateString: string | null): string => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    return `${day}/${month}`;
  };

  const getRiskScoreText = (score: number | null) => {
    if (score === null || score === undefined) return "N/A";
    if (score > 70) return "High";
    if (score > 30) return "Medium";
    return "Low";
  };

  const getRiskColor = (score: number | null) => {
    if (score === null || score === undefined) return "";
    if (score > 70) return "risk-score-red";
    if (score > 30) return "risk-score-amber";
    return "risk-score-green";
  };

  const formatObservationDateTime = (dateString: string | null): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const today = new Date();

    const isToday =
      date.getUTCDate() === today.getUTCDate() &&
      date.getUTCMonth() === today.getUTCMonth() &&
      date.getUTCFullYear() === today.getUTCFullYear();

    if (isToday) {
      const hh = String(date.getUTCHours()).padStart(2, "0");
      const mm = String(date.getUTCMinutes()).padStart(2, "0");
      const ss = String(date.getUTCSeconds()).padStart(2, "0");
      return `${hh}:${mm}:${ss} UTC`;
    }

    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    return `${day}/${month}`;
  };

  const updateVesselPositions = async (imos: string[]) => {
    const updatedVessels: TCurrentVessel[] = [];

    const apiClient = new ApiClient(apiUrl);

    if (imos.length === 0) {
      return [];
    }

    for (const imo of imos) {
      try {
        const [vesselStatic, position, riskScoreResponse] = await Promise.all([
          apiClient.getVesselStatic(imo),

          apiClient.getVesselPosition(imo),

          apiClient.getVesselRiskScore(imo),
        ]);

        let riskScore: any = null;
        let score: number | null = null;
        if (riskScoreResponse.data != "") {
          riskScore = riskScoreResponse.data.RiskScores.find(
            (r: any) => r.RiskPeriodInMonths === 6,
          );
          score = riskScore ? riskScore.TotalRiskScore : null;
        }

        const vesselWithPosition: TCurrentVessel = {
          ...vesselStatic.data,
          latitude: position.latitude,
          longitude: position.longitude,
          heading: position.heading,
          eta: position.eta,
          destination: position.destination,
          observationDateTime: position.observationDateTime,
          riskScore: typeof score === "number" ? score : 0,
          riskScoreDetails: riskScoreResponse.data,
        };

        updatedVessels.push(vesselWithPosition);
      } catch (error) {
        console.warn(`Failed to fetch data for vessel IMO: ${imo}`, error);
      }
    }

    return updatedVessels;
  };

  // Track clientDomain in state
  const [clientDomain, setClientDomain] = useState<string>("");

  useEffect(() => {
    const fetchFleets = async () => {
      try {
        const apiClient = new ApiClient(apiUrl);

        const user = localStorage.getItem("user");
        if (!user) {
          throw new Error("User not found in localStorage");
        }
        const userEmail = JSON.parse(user).email;
        if (!userEmail) {
          throw new Error("Email not found in User");
        }

        // Check if client is passed as a querystring parameter
        const urlParams = new URLSearchParams(window.location.search);
        let derivedClientDomain: string;

        if (urlParams.has("client")) {
          derivedClientDomain = urlParams.get("client")!.toLowerCase();
        } else {
          const clientUsersResponse = await apiClient.getClientUsers(userEmail);
          const clientUsers = clientUsersResponse.data;

          if (clientUsers.length === 0) {
            throw new Error("No clients found for the user");
          }

          derivedClientDomain = clientUsers[0].clientDomain;
        }

        setClientDomain(derivedClientDomain);

        const clientFleetsResponse =
          await apiClient.getClientFleets(derivedClientDomain);
        const clientFleets: BackendFleet[] = clientFleetsResponse.data;

        // Fetch and render vessel static data first
        const fleetsWithStaticVessels: Fleet[] = await Promise.all(
          clientFleets.map(async (fleet: BackendFleet) => {
            const staticVessels: TCurrentVessel[] = await Promise.all(
              fleet.vessels.map(async (imo: string) => {
                try {
                  const vesselStatic = await apiClient.getVesselStatic(imo);
                  return { ...vesselStatic.data, imo };
                } catch (err) {
                  return { imo, name: "Unknown Vessel" } as TCurrentVessel;
                }
              }),
            );
            return {
              id: fleet.id,
              name: fleet.name,
              vessels: staticVessels,
            };
          }),
        );
        setFleets(fleetsWithStaticVessels);

        // For each vessel, fetch position and risk score with limited concurrency
        const tasks: { fleetId: string; vessel: TCurrentVessel }[] = [];
        fleetsWithStaticVessels.forEach((fleet: Fleet) => {
          fleet.vessels.forEach((vessel: TCurrentVessel) => {
            tasks.push({ fleetId: fleet.id, vessel });
          });
        });

        const CONCURRENCY_LIMIT = 5;

        const runTask = async (fleetId: string, vessel: TCurrentVessel) => {
          try {
            const [position, riskScoreResponse] = await Promise.all([
              apiClient.getVesselPosition(vessel.imo),
              apiClient.getVesselRiskScore(vessel.imo),
            ]);

            let riskScore: any = null;
            let score: number | null = null;
            if (riskScoreResponse.data != "") {
              riskScore = riskScoreResponse.data.RiskScores?.find(
                (r: any) => r.RiskPeriodInMonths === 6,
              );
              score = riskScore ? riskScore.TotalRiskScore : null;
            }

            const updatedVessel: TCurrentVessel = {
              ...vessel,
              latitude: position.latitude,
              longitude: position.longitude,
              heading: position.heading,
              eta: position.eta,
              destination: position.destination,
              observationDateTime: position.observationDateTime,
              riskScore: typeof score === "number" ? score : 0,
              riskScoreDetails: riskScoreResponse.data,
            };

            setFleets((prevFleets) =>
              prevFleets.map((f) =>
                f.id === fleetId
                  ? {
                      ...f,
                      vessels: f.vessels.map((v) =>
                        v.imo === vessel.imo ? updatedVessel : v,
                      ),
                    }
                  : f,
              ),
            );
          } catch (err) {
            // Ignore errors for position/risk
          }
        };

        let currentIndex = 0;

        for (let i = 0; i < CONCURRENCY_LIMIT && i < tasks.length; i++) {
          (async () => {
            while (true) {
              const index = currentIndex++;
              if (index >= tasks.length) {
                break;
              }
              const { fleetId, vessel } = tasks[index];
              await runTask(fleetId, vessel);
            }
          })();
        }
      } catch (err) {
        console.error("Error fetching fleets:", err);
        setError("Failed to fetch fleets.");
      } finally {
        setLoading(false);
      }
    };
    fetchFleets();
  }, []);

  const toProperCase = (text: string) => {
    return text
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleAccordionChange = (panel: string) => {
    setExpanded(expanded === panel ? false : panel);
    setSelectedVesselForPlanning(null);
    setHoveredVesselImo(null);
  };

  const handleOpenAddVessel = (fleetName: string, clientFleetId: string) => {
    setSelectedFleet({ fleetName, clientFleetId });
    setIsAddVesselOpen(true);
  };

  const handleCloseAddVessel = () => {
    setIsAddVesselOpen(false);
    setSelectedFleet(null);
  };

  const handleAddVesselToFleet = async (
    clientFleetId: string,
    _newVessel: TCurrentVessel,
  ) => {
    try {
      const apiClient = new ApiClient(apiUrl);

      // await apiClient.addVesselToClientFleet(clientFleetId, newVessel.imo.toString());

      const updatedFleetResponse =
        await apiClient.getClientFleet(clientFleetId);

      const updatedFleet = updatedFleetResponse.data;

      const updatedVessels = await updateVesselPositions(updatedFleet.vessels);

      setFleets((prevFleets) =>
        prevFleets.map((fleet) =>
          fleet.id === clientFleetId
            ? { ...fleet, vessels: updatedVessels }
            : fleet,
        ),
      );

      onUpdateVessels(updatedVessels);
    } catch (error) {
      console.error("Failed to add vessel or refresh fleet:", error);
    }
  };

  const handleDeleteFleet = async (clientFleetId: string) => {
    try {
      const apiClient = new ApiClient(apiUrl);

      const response = await apiClient.deleteClientFleet(clientFleetId);

      if (response.status < 200 || response.status >= 300) {
        setError(response.data?.message || "Failed to delete fleet.");

        return;
      }

      setFleets((prevFleets) =>
        prevFleets.filter((fleet) => fleet.id !== clientFleetId),
      );
    } catch (error: any) {
      if (error?.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("Failed to delete fleet.");
      }
    }
  };

  const handleDeleteVessel = async (clientFleetId: string, imo: string) => {
    try {
      const apiClient = new ApiClient(apiUrl);

      const response = await apiClient.removeVesselFromClientFleet(
        clientFleetId,
        imo,
      );

      if (response.status < 200 || response.status >= 300) {
        setError(response.data?.message || "Failed to delete vessel.");
        return;
      }

      const updatedFleetResponse =
        await apiClient.getClientFleet(clientFleetId);

      const updatedFleet = updatedFleetResponse.data;

      const updatedVessels = await updateVesselPositions(updatedFleet.vessels);

      setFleets((prevFleets) =>
        prevFleets.map((fleet) =>
          fleet.id === clientFleetId
            ? { ...fleet, vessels: updatedVessels }
            : fleet,
        ),
      );

      onUpdateVessels(updatedVessels);
    } catch (error) {
      console.error("Failed to delete vessel or refresh fleet:", error);
    }
  };

  const handleFleetAdded = (fleet: {
    id: string;
    name: string;
    vessels: any[];
  }) => {
    setFleets((prevFleets) => [...prevFleets, fleet]);
  };

  const ConfirmModal: React.FC<{
    open: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    message: string;
  }> = ({ open, onConfirm, onCancel, message }) => {
    if (!open) return null;
    return (
      <div className="modal-overlay">
        <div className="modal-content-confirmation">
          <div className="modal-message">{message}</div>
          <div className="modal-actions">
            <button className="confirm-btn" onClick={onConfirm}>
              Yes
            </button>
            <button className="cancel-btn" onClick={onCancel}>
              No
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Helper to get vessel name by fleetId and imo
  const getVesselName = (fleetId: string, imo?: string) => {
    const fleet = fleets.find((f) => f.id === fleetId);
    if (!fleet || !imo) return "";
    const vessel = fleet.vessels.find((v) => v.imo === imo);
    return vessel?.name || "Unnamed Vessel";
  };

  // Helper to get fleet name by fleetId
  const getFleetName = (fleetId: string) => {
    const fleet = fleets.find((f) => f.id === fleetId);
    return fleet?.name || "";
  };

  const handleShowRiskScoreDetails = async (imo: string) => {
    try {
      const apiClient = new ApiClient(apiUrl);
      const response = await apiClient.getVesselRiskScore(imo);

      if (response.status < 200 || response.status >= 300) {
        setError(response.data?.message || "Failed to get risk score");
        return;
      }

      setRiskScoreDetailsModal({
        open: true,
        details: response.data,
      });
    } catch (error) {
      console.error("Failed to fetch risk score details:", error);
      alert("Failed to fetch risk score details. Please try again.");
    }
  };

  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  return (
    <div className="client-fleet-container">
      {fleets.map((fleet, index) => (
        <div
          key={index}
          className={`accordion ${expanded === fleet.name ? "expanded" : ""}`}
        >
          <div
            className="accordion-header"
            onClick={(e) => {
              e.stopPropagation();
              handleAccordionChange(fleet.name);
              onUpdateVessels(fleet.vessels);
            }}
          >
            <span className="fleet-name">{fleet.name}</span>
            <span className="accordion-icon">
              {expanded === fleet.name ? "-" : "+"}
            </span>
          </div>
          {expanded === fleet.name && (
            <div className="accordion-body">
              <button
                className="secondary-button vessel-add-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenAddVessel(fleet.name, fleet.id);
                }}
              >
                + Add Vessel
              </button>
              <div className="vessels-header"></div>
              {fleet.vessels.length > 0 ? (
                <div className="vessel-table">
                  <div>
                    {fleet.vessels.map((vessel, vesselIndex) => {
                      const isSelected =
                        selectedVesselForPlanning?.imo === vessel.imo;

                      return (
                        <React.Fragment key={vesselIndex}>
                          <div
                            className={`vessel-row ${isSelected ? "selectedBox" : "unselectedBox"}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onUpdateVessels([vessel]);
                              setSelectedVesselForPlanning((prev) =>
                                prev?.imo === vessel.imo ? null : vessel,
                              );
                            }}
                            style={{ cursor: "pointer", position: "relative" }}
                          >
                            <div className="vessel-cell">
                              <span className="vessel-name-row">
                                <VeselIcon
                                  size={24}
                                  color={
                                    vessel.riskScore !== null &&
                                    vessel.riskScore !== undefined
                                      ? getRiskColor(vessel.riskScore) ===
                                        "risk-score-red"
                                        ? "#ff3333"
                                        : getRiskColor(vessel.riskScore) ===
                                            "risk-score-amber"
                                          ? "#ff9900"
                                          : getRiskColor(vessel.riskScore) ===
                                              "risk-score-green"
                                            ? "#2ecc40"
                                            : "black"
                                      : "black"
                                  }
                                />
                                <span className="vessel-name">
                                  {vessel.name || "Unnamed Vessel"}
                                </span>
                                <span className="vessel-imo">
                                  {vessel.imo ? ` (${vessel.imo})` : ""}
                                </span>
                                <span className="vessel-dwt">
                                  {vessel.dwt
                                    ? ` ${Math.round(vessel.dwt / 1000)}K`
                                    : "N/A"}
                                </span>
                              </span>
                              {/* Details: show only when selected (clicked) */}
                              {isSelected && (
                                <div className="vessel-details-group">
                                  <hr />
                                  <div className="vessel-details">
                                    <div>
                                      <span className="label">Type:</span>{" "}
                                      <span className="vessel-type">
                                        {vessel.type && vessel.type.length > 16
                                          ? `${toProperCase(vessel.type.substring(0, 16))}...`
                                          : toProperCase(
                                              vessel.type || "Unknown",
                                            )}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="label">DWT:</span>{" "}
                                      <span className="vessel-dwt">
                                        {vessel.dwt
                                          ? ` ${Math.round(vessel.dwt / 1000)}K`
                                          : "N/A"}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="label">
                                        Destination:
                                      </span>{" "}
                                      <span className="vessel-destination">
                                        {vessel.destination || "N/A"}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="label">ETA:</span>{" "}
                                      <span className="vessel-eta">
                                        {formatDateToDDMM(vessel.eta)}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="label">Last seen:</span>{" "}
                                      <span className="vessel-lastseen">
                                        {formatObservationDateTime(
                                          vessel.observationDateTime,
                                        )}
                                      </span>
                                    </div>
                                    <div className="risk-score-row">
                                      <span className="label">Risk Score:</span>{" "}
                                      {vessel.riskScore !== null ? (
                                        <span
                                          className={getRiskColor(
                                            vessel.riskScore,
                                          )}
                                          style={{
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleShowRiskScoreDetails(
                                              vessel.imo,
                                            );
                                          }}
                                        >
                                          {getRiskScoreText(vessel.riskScore)}
                                        </span>
                                      ) : (
                                        <span>N/A</span>
                                      )}
                                      <div className="vessel-delete-btn-row">
                                        <button
                                          className="vessel-delete-button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setConfirmDelete({
                                              fleetId: fleet.id,
                                              imo: vessel.imo,
                                            });
                                          }}
                                        >
                                          <BinIcon color="black" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                  <hr />
                                  <div>
                                    <PortPlanner
                                      vessel={vessel}
                                      onClose={() =>
                                        setSelectedVesselForPlanning(null)
                                      }
                                      setRouteCoordinates={setRouteCoordinates}
                                      setRouteColors={setRouteColors}
                                      setBunkerMarkers={setBunkerMarkers}
                                      setPrices={setPrices}
                                      setCrudePrice={setCrudePrice}
                                      setWeather={setWeather}
                                      distanceNm={distanceNm}
                                      onSelectPortCall={handleSelectPortCall}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          {/* ...existing code... */}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="no-vessels-message">
                  No vessels in this fleet.
                </div>
              )}
              <button
                className="fleet-delete-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDeleteFleet({ fleetId: fleet.id });
                }}
                title="Delete Fleet"
              >
                <BinIcon color="black" />
              </button>
            </div>
          )}
        </div>
      ))}

      {isAddVesselOpen && selectedFleet && (
        <div className="dialog">
          <AddVessel
            fleetName={selectedFleet.fleetName}
            clientFleetId={selectedFleet.clientFleetId}
            onCancel={handleCloseAddVessel}
            onAddVessel={handleAddVesselToFleet}
          />
        </div>
      )}
      <AddFleet
        apiUrl={apiUrl}
        clientDomain={clientDomain}
        onFleetAdded={handleFleetAdded}
      />

      {/* Confirm modal for vessel deletion */}
      <ConfirmModal
        open={!!confirmDelete && !!confirmDelete.imo}
        message={
          confirmDelete && confirmDelete.imo
            ? `Are you sure you want to delete the vessel "${getVesselName(confirmDelete.fleetId, confirmDelete.imo)}"?`
            : "Are you sure you want to delete this vessel?"
        }
        onConfirm={async () => {
          if (confirmDelete && confirmDelete.imo) {
            await handleDeleteVessel(confirmDelete.fleetId, confirmDelete.imo);
            setConfirmDelete(null);
          }
        }}
        onCancel={() => setConfirmDelete(null)}
      />

      {/* Confirm modal for fleet deletion */}
      <ConfirmModal
        open={!!confirmDeleteFleet}
        message={
          confirmDeleteFleet
            ? `Are you sure you want to delete the fleet "${getFleetName(confirmDeleteFleet.fleetId)}"?`
            : "Are you sure you want to delete this fleet?"
        }
        onConfirm={async () => {
          if (confirmDeleteFleet) {
            await handleDeleteFleet(confirmDeleteFleet.fleetId);
            setConfirmDeleteFleet(null);
          }
        }}
        onCancel={() => setConfirmDeleteFleet(null)}
      />

      {/* Risk Score Details Modal */}
      <RiskScoreDetailsModal
        open={riskScoreDetailsModal.open}
        details={riskScoreDetailsModal.details}
        onClose={() => setRiskScoreDetailsModal({ open: false, details: null })}
      />

      {error && <FeedbackText message={error} />}
    </div>
  );
};

export default ClientFleet;
